import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import ConfirmationDialog from '../components/ConfirmationDialog';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import { validateRoutingPolicy } from '../utils/routingPolicyValidator';
import { compilePolicyCriteria } from '../utils/policyCriteriaCompiler';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const createStep = (step) => ({
  step: step,
  routingGroupId: '',
  timeoutSeconds: ''
});

const formatPolicySteps = (steps) => {
  return (steps || [])
    .map((step) => `${step.step}. ${step.routingGroupId} (${step.timeoutSeconds == null ? 'no timeout' : `${step.timeoutSeconds}s`})`)
    .join(' -> ') || '-';
};

const getPolicyStatusLabel = (policy) => {
  const status = policy.status ?? policy.active;
  if (typeof status === 'boolean') return status ? 'Active' : 'Inactive';
  return String(status || '').toLowerCase() === 'inactive' ? 'Inactive' : 'Active';
};

const getSortValue = (policy, key) => {
  if (key === 'routingSteps') {
    return formatPolicySteps(policy.routingSteps).toLowerCase();
  }
  if (key === 'status') {
    return getPolicyStatusLabel(policy).toLowerCase();
  }
  return String(policy[key] ?? '').toLowerCase();
};

const RoutingPoliciesPage = () => {
  const [routingPolicies, setRoutingPolicies] = useState([]);
  const [routingGroups, setRoutingGroups] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [instances, setInstances] = useState([]);
  const [policyForm, setPolicyForm] = useState({
    policyId: '',
    policyName: '',
    instanceId: '',
    routingSteps: [createStep(1)],
    active: true
  });
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('Routing Policy JSON Preview');
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, routingGroup: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  
 
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();


  const endpoints = getApiEndpoints(selectedEnvironment);

  const getUserRole = () => (
    JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  );

  // Handle environment change
  const handleEnvironmentChange = (e) => {
      const newEnvironment = e.target.value;
      setSelectedEnvironment(newEnvironment);
      saveSelectedEnvironment(newEnvironment);
      setResult(null);
      setError(null);
   };  


  const getPreviewPayload = () => ({
    policyId: policyForm.policyId,
    policyName: policyForm.policyName,
    instanceId: policyForm.instanceId,
    routingSteps: policyForm.routingSteps.map((step) => ({
      step: step.step,
      routingGroupId: step.routingGroupId,
      timeoutSeconds: step.timeoutSeconds === '' ? null : Number(step.timeoutSeconds)
    })),
    status: policyForm.active ? 'Active' : 'Inactive',
    user_role: getUserRole()
  });

  const fetchPolicyData = async (instanceId) => {
    setLoadingData(true);
    setRoutingGroups([]);
    setRoutingPolicies([]);
    setAttributes([]);

    try {
      const [groupsResponse, policiesResponse, attributesResponse] = await Promise.all([
        apiService.post(endpoints.routingGroups, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.routingPolicies, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.attributesView, {
          instance_id: instanceId,
          user_role: getUserRole()
        })
      ]);

      if (groupsResponse.data.status !== 'ok' || policiesResponse.data.status !== 'ok' || attributesResponse.data.status !== 'ok') {
        throw new Error('Failed to load routing policy data');
      }

      setRoutingGroups(groupsResponse.data.result?.routingGroups || []);
      setRoutingPolicies(policiesResponse.data.result?.routingPolicies || []);
      setAttributes(attributesResponse.data.result?.attributes || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load routing policy data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInstanceChange = (e) => {
    const instanceId = e.target.value;
    setPolicyForm((prev) => ({
      ...prev,
      policyId: '',
      policyName: '',
      instanceId,
      routingSteps: [createStep(1)],
      status: policyForm.active ? 'Active' : 'Inactive',
    }));
    setError(null);
    setResult(null);

    if (instanceId) {
      fetchPolicyData(instanceId);
    } else {
      setRoutingGroups([]);
      setRoutingPolicies([]);
      setAttributes([]);
    }
  };

  const updateStep = (index, field, value) => {
    setPolicyForm((prev) => ({
      ...prev,
      routingSteps: prev.routingSteps.map((step, stepIndex) => (
        stepIndex === index ? { ...step, [field]: value } : step
      ))
    }));
  };

  const addStep = () => {
    setPolicyForm((prev) => ({
      ...prev,
      routingSteps: [...prev.routingSteps, createStep(prev.routingSteps.length + 1)]
    }));
  };

  const removeStep = (index) => {
    setPolicyForm((prev) => ({
      ...prev,
      routingSteps: prev.routingSteps
        .filter((_, stepIndex) => stepIndex !== index)
        .map((step, stepIndex) => ({ ...step, step: stepIndex + 1 }))
    }));
  };

  const validateCurrentPolicy = () => {
    setError(null);
    setResult(null);
    const validation = validateRoutingPolicy(getPreviewPayload(), routingGroups, attributes);
    if (!validation.valid) {
      setError(validation.errors.map(({ message }) => message).join(' '));
      return false;
    }
    setResult({ message: 'Routing policy is valid.' });
    return true;
  };

  const showPolicyPreview = () => {
    setPreviewTitle('Routing Policy JSON Preview');
    setPreviewData(getPreviewPayload());
    setShowPreview(true);
  };

  const compileCurrentPolicy = () => {
    setError(null);
    setResult(null);
    try {
      setPreviewTitle('AWS Connect Routing Criteria');
      setPreviewData(compilePolicyCriteria(getPreviewPayload(), routingGroups, attributes));
      setShowPreview(true);
    } catch (err) {
      const messages = Array.isArray(err.errors)
        ? err.errors.map(({ message }) => message).join(' ')
        : err.message;
      setError(messages || 'Failed to compile routing policy.');
    }
  };

  const loadRoutingPolicy = (policy) => {
    setPolicyForm({
      policyId: policy.policyId || '',
      policyName: policy.policyName || '',
      instanceId: policyForm.instanceId,
      routingSteps: (policy.routingSteps || []).map((step, index) => ({
        step: Number(step.step ?? step.priority ?? index + 1),
        routingGroupId: step.routingGroupId || '',
        timeoutSeconds: step.timeoutSeconds == null ? '' : step.timeoutSeconds
      })),
      status: policyForm.active ? 'Active' : 'Inactive'
    });
    setError(null);
    setResult({ message: `Loaded routing policy '${policy.policyId}'.` });
  };

  const handleRoutingPolicyChange = (e) => {
    const { name, value,checked, type } = e.target;

    setPolicyForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
   
  };

  const handleDeleteRoutingPolicy = (routingPolicy) => {
    setConfirmDialog({isOpen: true, routingPolicy});
  };

  const confirmDeleteRoutingPolicy = async () => {
    const routingPolicy = confirmDialog.routingPolicy;
    setConfirmDialog({isOpen: false, routingPolicy: null});
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(`${endpoints.deleteRoutingPolicy}/${routingPolicy.policyId}`, {
        instanceId: routingPolicy.instanceId,
        policyId: routingPolicy.policyId,
        user_role: user?.role || ''
      })
        
      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to delete routing policy');
      }
      await fetchPolicyData(routingPolicy.instanceId);
      setResult({ status: 'ok', message: `Routing policy '${routingPolicy.policyId}' deleted successfully.` });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete routing policy');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteRoutingPolicy = () => {
    setConfirmDialog({ isOpen: false, routingPolicy: null });
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };  

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  
  const filteredRoutingPolicies = React.useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return routingPolicies;
      return routingPolicies.filter((policy) => {
        return [
          policy.policyName,
          policy.policyId,
          policy.instanceId,
          getPolicyStatusLabel(policy),
          formatPolicySteps(policy.routingSteps),
          ...(policy.routingSteps || []).flatMap((step) => [
            step.step,
            step.priority,
            step.routingGroupId,
            step.timeoutSeconds == null ? 'no timeout' : `${step.timeoutSeconds}s`,
          ])
        ].some((value) => String(value || '').toLowerCase().includes(term));
      });
    }, [routingPolicies, searchTerm]);

  const sortedRoutingPolicies = React.useMemo(() => {
      if (!sortKey) return filteredRoutingPolicies;
      const copy = [...filteredRoutingPolicies];
      copy.sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    }, [filteredRoutingPolicies, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRoutingPolicies.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRoutingPolicies = sortedRoutingPolicies.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  }; 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const validation = validateRoutingPolicy(getPreviewPayload(), routingGroups, attributes);
      if (!validation.valid) {
        throw new Error(validation.errors.map(({ message }) => message).join(' '));
      }

      const response = await apiService.post(
        endpoints.routingPolicyRegistry,
        getPreviewPayload()
      );

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to create routing policy');
      }

      await fetchPolicyData(policyForm.instanceId);
      setPolicyForm((prev) => ({
        ...prev,
        policyId: '',
        policyName: '',
        routingSteps: [createStep(1)],
        status: policyForm.active ? 'Active' : 'Inactive',
      }));
      setResult({ message: 'Routing policy created successfully.' });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create routing policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInstances = async () => {
      setLoadingInstances(true);

      try {
        const response = await apiService.get(endpoints.instanceDropdown);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load instances');
      } finally {
        setLoadingInstances(false);
      }
    };

    loadInstances();
  }, [endpoints.instanceDropdown]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Routing Policies</h1>
          <p className="page-subtitle">
            Build ordered policies from predefined routing groups
          </p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
          ← Back to Routing Administration
        </Link>
      </div>

    {/* Environment Selector */}
    <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
        }}>
        <label htmlFor="environment" style={{ 
            fontWeight: 'bold', 
            fontSize: '16px',
            minWidth: '120px'
        }}>
            Environment:
        </label>
        <select
            id="environment"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
            }}
        >
            {availableEnvironments.map(env => (
            <option key={env} value={env}>
                {ENVIRONMENT_LABELS[env]} ({env})
            </option>
            ))}
        </select>
        <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
            {selectedEnvironment}
        </span>
    </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-section">
            <h3>New Routing Policy</h3>

            <div className="form-group">
                <label htmlFor="instanceId">Instance *</label>
                <select
                  id="instanceId"
                  value={policyForm.instanceId}
                  onChange={handleInstanceChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
            </div>  

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="policyId">Policy ID *</label>
                    <input
                    id="policyId"
                    value={policyForm.policyId}
                    onChange={(e) => setPolicyForm((prev) => ({ ...prev, policyId: e.target.value }))}
                    required
                    maxLength={256}
                    placeholder="policy-003"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="policyName">Policy Name *</label>
                    <input
                    id="policyName"
                    value={policyForm.policyName}
                    onChange={(e) => setPolicyForm((prev) => ({ ...prev, policyName: e.target.value }))}
                    required
                    maxLength={256}
                    placeholder="Policy Name"
                    />
                </div>
            </div>

            <fieldset className="allowed-values-fieldset">
              <legend>Routing Steps *</legend>
              {policyForm.routingSteps.map((step, index) => (
                <div className="allowed-value-row" key={step.step}>
                  <div className="form-group">
                    <label htmlFor={`routingGroupId-${index}`}>
                      Step {step.step}
                    </label>
                    <select
                      id={`routingGroupId-${index}`}
                      value={step.routingGroupId}
                      onChange={(e) => updateStep(index, 'routingGroupId', e.target.value)}
                      required
                      disabled={!policyForm.instanceId || loadingData}
                    >
                      <option value="">
                        {loadingData ? 'Loading routing groups...' : 'Select a routing group'}
                      </option>
                      {routingGroups
                        .filter((group) => group.active !== false)
                        .map((group) => (
                          <option key={group.routingGroupId} value={group.routingGroupId}>
                            {group.routingGroupId} ({group.displayName})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor={`timeoutSeconds-${index}`}>Timeout (seconds)</label>
                    <input
                      id={`timeoutSeconds-${index}`}
                      type="number"
                      min="0"
                      value={step.timeoutSeconds}
                      onChange={(e) => updateStep(index, 'timeoutSeconds', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary allowed-value-remove"
                    onClick={() => removeStep(index)}
                    disabled={policyForm.routingSteps.length === 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addStep}
                disabled={!policyForm.instanceId || loadingData}
              >
                Add Routing Step
              </button>
            </fieldset>
            <div className="form-group attribute-active-control">
                <label htmlFor="active">
                  <input
                    id="active"
                    name="active"
                    type="checkbox"
                    checked={policyForm.active}
                    onChange={handleRoutingPolicyChange}
                  />
                  Active
                </label>
              </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={showPolicyPreview}
            >
              Preview JSON
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={validateCurrentPolicy}
              disabled={loadingData}
            >
              Validate Policy
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={compileCurrentPolicy}
              disabled={loadingData}
            >
              Compile AWS Criteria
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || loadingData}
            >
              {loading ? 'Creating...' : 'Create Routing Policy'}
            </button>
          </div>
        </form>
      </div>

      {showPreview && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="policy-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="policy-preview-title">{previewTitle}</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(previewData, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {routingPolicies.length > 0 && (
        <div className="table-format-list">
          <div className="table-format-list-header">
            <h3>Existing Routing Policies ({routingPolicies.length}{sortedRoutingPolicies.length !== routingPolicies.length ? ` of ${routingPolicies.length}` : ''})</h3>
            <div className="table-format-list-controls">
              <input
                type="text"
                className="table-format-search-input"
                placeholder="Search Routing Policies..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
              <div className="page-size-selector">
                <label htmlFor="page_size">Rows per page:</label>
                <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('policyName')}>Policy Name{sortIndicator('policyName')}</th>
                <th className="sortable" onClick={() => handleSort('policyId')}>Policy ID{sortIndicator('policyId')}</th>
                <th className="sortable" onClick={() => handleSort('instanceId')}>Instance{sortIndicator('instanceId')}</th>
                <th className="sortable" onClick={() => handleSort('routingSteps')}>Routing Steps{sortIndicator('routingSteps')}</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRoutingPolicies.map((policy, index) => (
                <tr key={`${policy.policyId || policy.instanceId}-${index}`}>
                  <td>{policy.policyName || '-'}</td>
                  <td>{policy.policyId || '-'}</td>
                  <td>{policy.instanceId || '-'}</td>
                  <td>
                    {policy.routingSteps ? formatPolicySteps(policy.routingSteps) : '-'}
                  </td>
                  <td>{getPolicyStatusLabel(policy)}</td>
                  <td>
                    <button type="button"className="btn btn-secondary" onClick={() => loadRoutingPolicy(policy)}>
                      Load
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => handleDeleteRoutingPolicy(policy)} style={{marginLeft: '4px'}}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
        </div>
      )}

      {!loadingData && policyForm.instanceId && routingPolicies.length === 0 && (
        <div className="attribute-list">
          <h3>Existing Routing Policies (0)</h3>
          <p>No routing policies found.</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>{result.message}</p>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Routing Policy"
        message={`Are you sure you want to delete routing policy '${confirmDialog.routingPolicy?.policyId}'? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDanger={true}
        onConfirm={confirmDeleteRoutingPolicy}
        onCancel={cancelDeleteRoutingPolicy}
      />

    </div>
  );
};

export default RoutingPoliciesPage;